import React, { useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Handle, Position,
  MarkerType, useNodesState, useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import api, {
  tSimState, tEventType, tOrigin, TRANSITION_ORIGIN, SIM_STATE_STYLE, simStateColor, simStateCls,
  INHERENT_LEVEL, tInherentLevel, tRiskConfidence,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Activity, ArrowLeft, ArrowRight, Loader2, Server, Target, ShieldCheck, GitBranch, AlertTriangle, Info, Gauge,
} from "lucide-react";

function SimNode({ data }) {
  const st = data.simulated_state;
  const s = SIM_STATE_STYLE[st] || SIM_STATE_STYLE.OPERATIONAL;
  const Icon = data.type === "service" ? Target : Server;
  return (
    <div
      className={`rounded-sm border px-3 py-2 min-w-[150px] bg-card ${data.changed ? "ring-2" : ""}`}
      style={{ borderColor: s.color, boxShadow: data.changed ? `0 0 0 1px ${s.color}` : "none" }}
      data-testid={`sim-node-${data.id}`}
    >
      <Handle type="target" position={Position.Top} className="!bg-zinc-600" />
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" style={{ color: s.color }} />
        <span className="text-xs font-medium truncate max-w-[120px]">{data.label}</span>
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
        <span className="text-[10px] font-mono" style={{ color: s.color }}>{tSimState(st)}</span>
        {data.changed && <span className="text-[9px] text-muted-foreground">(baseline {tSimState(data.baseline_state)})</span>}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-zinc-600" />
    </div>
  );
}
const NODE_TYPES = { sim: SimNode };

function layout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", nodesep: 60, ranksep: 90 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => g.setNode(n.id, { width: 170, height: 60 }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const p = g.node(n.id);
    return { ...n, position: { x: p.x - 85, y: p.y - 30 } };
  });
}

function OverlayCanvas({ graph }) {
  const rfNodes = useMemo(() => {
    const laid = layout(
      (graph.nodes || []).map((n) => ({
        id: n.id, type: "sim",
        data: { id: n.id, label: n.name, type: n.type, simulated_state: n.simulated_state,
                baseline_state: n.baseline_state, changed: n.changed },
        position: { x: 0, y: 0 },
      })),
      graph.edges || [],
    );
    return laid;
  }, [graph]);
  const rfEdges = useMemo(
    () => (graph.edges || []).map((e) => ({
      id: e.id, source: e.source, target: e.target, type: "smoothstep",
      markerEnd: { type: MarkerType.ArrowClosed, color: "#52525B" },
      style: { stroke: "#3F3F46", strokeWidth: 1.5 },
    })),
    [graph],
  );
  const [nodes, , onNodesChange] = useNodesState(rfNodes);
  const [edges, , onEdgesChange] = useEdgesState(rfEdges);
  return (
    <div style={{ height: 460 }} className="border border-border rounded-sm bg-background" data-testid="simulation-overlay-canvas">
      <ReactFlow
        nodes={nodes} edges={edges} nodeTypes={NODE_TYPES}
        onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
        fitView minZoom={0.3} maxZoom={1.6} proOptions={{ hideAttribution: true }}
      >
        <Background color="#27272A" gap={24} />
        <Controls className="!bg-card" showInteractive={false} />
        <MiniMap nodeColor={(n) => simStateColor(n.data?.simulated_state)} className="!bg-card" pannable zoomable />
      </ReactFlow>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children, note }) {
  return (
    <section className="border border-border bg-card rounded-sm" data-testid={testid}>
      <div className="px-6 py-4 border-b border-border flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="font-head font-semibold text-sm">{title}</h2>
      </div>
      <div className="p-6 space-y-3">
        {note && <p className="text-[11px] text-muted-foreground leading-snug">{note}</p>}
        {children}
      </div>
    </section>
  );
}

export default function SimulationResult() {
  const { runId } = useParams();
  const navigate = useNavigate();

  const { data: run, isLoading } = useQuery({
    queryKey: ["simulation", runId],
    queryFn: async () => (await api.get(`/simulations/${runId}`)).data,
  });
  const { data: overlay } = useQuery({
    enabled: !!run,
    queryKey: ["simulation-overlay", runId],
    queryFn: async () => (await api.get(`/scenarios/${run.scenario_id}/impact-graph?run_id=${runId}`)).data,
  });

  const { can } = useAuth();
  const qc = useQueryClient();
  const canAssess = can("risk:assess");
  const { data: residuals } = useQuery({
    enabled: !!run,
    queryKey: ["residuals", run?.scenario_id],
    queryFn: async () => (await api.get(`/scenarios/${run.scenario_id}/residual`)).data,
  });
  const residualsForRun = useMemo(
    () => (residuals || []).filter((r) => r.simulation_run_id === runId),
    [residuals, runId],
  );
  const residualM = useMutation({
    mutationFn: async () => api.post(`/scenarios/${run.scenario_id}/residual`, { run_id: runId }),
    onSuccess: () => { toast.success("Risque résiduel calculé"); qc.invalidateQueries({ queryKey: ["residuals", run.scenario_id] }); },
    onError: (e) => toast.error(String(e.response?.data?.detail || "Erreur de calcul du résiduel")),
  });

  const sortedTransitions = useMemo(() => {
    return [...(run?.transitions || [])].sort(
      (a, b) => (a.depth - b.depth) || ((a.sequence ?? 0) - (b.sequence ?? 0)),
    );
  }, [run]);

  if (isLoading) return <AppShell title="Simulation"><div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div></AppShell>;
  if (!run) return <AppShell title="Simulation"><p className="text-sm text-muted-foreground">Simulation introuvable.</p></AppShell>;

  const stats = run.stats || {};

  return (
    <AppShell title={run.scenario_title_snapshot || "Simulation"}
      subtitle="Résultat de simulation what-if (Lot 7) — snapshot isolé et immuable. Aucune modification de l'état réel, aucun risque résiduel."
      actions={
        <Button size="sm" variant="outline" className="rounded-sm gap-2" onClick={() => navigate(`/scenarios/${run.scenario_id}`)} data-testid="back-to-scenario-button">
          <ArrowLeft className="h-4 w-4" /> Retour au scénario
        </Button>
      }>
      <div className="space-y-6" data-testid="simulation-result">
        <Section title="Exécution" icon={Info} testid="section-sim-meta">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Moteur</div><div className="mt-1 font-mono text-xs">{run.engine_version}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Vocabulaire</div><div className="mt-1 font-mono text-xs">{run.vocab_version}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Profondeur</div><div className="mt-1 font-mono text-xs">{run.params?.max_depth} (atteinte {stats.depth_reached})</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Arrêt</div><div className="mt-1">{stats.truncated ? <Badge variant="outline" className="rounded-sm text-[10px] text-orange-400 border-orange-500/30" data-testid="sim-truncated-badge">tronqué (max_depth)</Badge> : <span className="text-xs text-green-400 font-mono">naturel</span>}</div></div>
          </div>
          <p className="text-[11px] text-muted-foreground font-mono border-t border-border pt-3">Empreinte reproductible : <span className="text-foreground">{(run.input_fingerprint || "").slice(0, 24)}…</span></p>
        </Section>

        <Section title={`Services impactés — ${(run.impacted_services || []).length}`} icon={AlertTriangle} testid="section-sim-impacted"
          note="État de service DÉRIVÉ de l'état des actifs supportants (oracle de résilience). Ce n'est ni un score, ni un risque résiduel.">
          {(run.impacted_services || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucun service impacté.</p> : (
            <div className="space-y-2">
              {run.impacted_services.map((s) => (
                <div key={s.service_id} className="border border-border rounded-sm px-3 py-2" data-testid={`sim-impacted-${s.service_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium flex items-center gap-2"><Target className="h-3.5 w-3.5 text-primary" /> {s.name}</span>
                    <Badge variant="outline" className={`rounded-sm font-mono text-xs ${simStateCls(s.state)}`}>{tSimState(s.state)}</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{s.reason}</p>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Overlay cartographique explicable" icon={GitBranch} testid="section-sim-overlay"
          note="Vue dérivée du run (versionnée, lecture seule) : anneau = nœud dont l'état simulé diffère de la baseline. Ne modifie jamais le graphe réel.">
          {overlay?.data ? <OverlayCanvas graph={overlay.data} /> : <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement de l'overlay…</div>}
          <div className="flex flex-wrap gap-3 pt-2">
            {Object.entries(SIM_STATE_STYLE).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} /> {v.label}
              </span>
            ))}
          </div>
        </Section>

        <Section title={`Causal trace — ${sortedTransitions.length} transition(s)`} icon={ShieldCheck} testid="section-sim-trace"
          note="« Pourquoi ce nœud est-il dans cet état ? » — chaîne causale déterministe. Chaque transition distingue INJECTÉ (hypothèse) vs DÉRIVÉ (propagation), avec sa règle (rule_id) et sa justification.">
          {sortedTransitions.length === 0 ? <p className="text-sm text-muted-foreground">Aucune transition.</p> : (
            <ol className="space-y-2">
              {sortedTransitions.map((t) => (
                <li key={t.transition_id} className="border border-border rounded-sm px-3 py-2" data-testid={`sim-transition-${t.transition_id}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="outline" className={`rounded-sm text-[10px] font-mono ${TRANSITION_ORIGIN[t.origin]?.cls || ""}`}>{tOrigin(t.origin)}</Badge>
                    <span className="font-mono text-muted-foreground">profondeur {t.depth}</span>
                    <Badge variant="outline" className="rounded-sm text-[10px] font-mono text-primary">{t.rule_id}</Badge>
                    {t.event_type && <span className="font-mono text-[11px]">{tEventType(t.event_type)}</span>}
                    <span className="font-medium">{t.target_name}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {t.previous_state ? tSimState(t.previous_state) : "—"} → <span className="text-foreground">{t.new_state ? tSimState(t.new_state) : "—"}</span>
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{t.reason}</p>
                  {t.control && (
                    <p className="text-[10px] text-primary/80 mt-1 font-mono">
                      Contrôle « {t.control.name} » ({t.control.function}) — {t.control.evidence?.resulting_effect} · attesté={String(t.control.evidence?.attested)} · créditable={String(t.control.evidence?.creditable)}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Section>

        <Section title="Risque résiduel (Lot 8)" icon={Gauge} testid="section-sim-residual"
          note="Dérivé du risque INHÉRENT (Lot 4) + preuves de CE run (immuable) + contrôles créditables & applicables + contexte de résilience (séparé). Jamais un simple « inhérent − efficacité » ; n'écrase jamais l'inhérent ; réévaluation conjointe vraisemblance/impact bornée.">
          {canAssess && (
            <Button size="sm" className="rounded-sm gap-2" disabled={residualM.isPending}
              onClick={() => residualM.mutate()} data-testid="compute-residual-button">
              {residualM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gauge className="h-4 w-4" />} Calculer le risque résiduel
            </Button>
          )}
          {residualsForRun.length === 0 ? (
            <p className="text-sm text-muted-foreground">{canAssess ? "Aucun résiduel calculé pour ce run." : "Aucun résiduel calculé. Permission risk:assess requise."}</p>
          ) : (
            <div className="space-y-3">
              {residualsForRun.map((r) => (
                <div key={r.id} className="border border-border rounded-sm p-3" data-testid={`residual-item-${r.service_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium flex items-center gap-2"><Target className="h-3.5 w-3.5 text-primary" /> {r.service_name}</span>
                    <span className="flex items-center gap-2 text-xs font-mono">
                      <Badge variant="outline" className={`rounded-sm ${INHERENT_LEVEL[r.inherent_level]?.cls || ""}`}>inhérent {tInherentLevel(r.inherent_level)}</Badge>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                      <Badge variant="outline" className={`rounded-sm ${INHERENT_LEVEL[r.level]?.cls || ""}`} data-testid={`residual-level-${r.service_id}`}>résiduel {tInherentLevel(r.level)}</Badge>
                      <span className="text-muted-foreground">conf. {tRiskConfidence(r.confidence)}</span>
                    </span>
                  </div>
                  {r.based_on_truncated_run && <p className="text-[10px] text-orange-400 mt-1 font-mono">Basé sur une simulation tronquée — résultat conservateur.</p>}
                  <details className="mt-2" data-testid={`residual-trace-${r.service_id}`}>
                    <summary className="text-[11px] text-primary cursor-pointer">Pourquoi ce niveau ? (causal trace)</summary>
                    <ol className="mt-2 space-y-1">
                      {(r.trace || []).map((t, i) => (
                        <li key={i} className="text-[11px] text-muted-foreground leading-snug">
                          <span className="font-mono text-primary">{t.rule_id}</span> — {t.justification}
                          {t.from != null && <span className="font-mono text-foreground"> [{String(t.from)} → {String(t.to)}]</span>}
                        </li>
                      ))}
                    </ol>
                    {(r.non_credited_controls || []).length > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-2">Contrôles NON crédités : {(r.non_credited_controls).map((c) => c.name || c.control_id).join(", ")}</p>
                    )}
                  </details>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </AppShell>
  );
}
